/**
 * Date range picker + presets. UTC datetime-local inputs plus one preset row:
 * the long presets for a source with history (EOS), the short ones (5 minutes
 * to a day) for a source that keeps only a rolling window (Live). Editing an
 * input flips to the `custom` preset and re-estimates.
 *
 * Which window it edits is the `scope` property: Explore's date atoms by
 * default, and a detached dashboard's own stored window when a dashboard passes
 * its own (`selection-scope.ts`). The short presets and "All data" belong to
 * Explore's scope - one follows Explore's data source, the other needs the
 * loaded ship's metadata - so neither is offered to any other scope.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  startDate,
  endDate,
  activePreset,
  shipDateRange,
  activeDataSource,
  type DatePreset,
} from '../../state/store.ts';
import { exploreSelection, type SelectionScope } from './selection-scope.ts';
import type { WindowShift } from '../../utils/dates.ts';
import { dataSources, sourceHasHistory } from '../../state/data-sources.ts';
import { periodComparison } from '../../state/period-comparison.ts';
import { matchingDatePreset, PRESET_LABELS } from '../../utils/dates.ts';
import './period-comparison-editor.ts';

/**
 * The everyday windows, as short chips so the row fits the sidebar on one line.
 * `24h` is rolling (the last 24 hours to now); the rest end at midnight
 * tomorrow UTC so today is always included. `1mo` is a calendar month, not 30
 * days - `30d` still restores from older links, it is just no longer offered.
 */
const HIST_PRESETS: Array<{ id: DatePreset; label: string; title: string }> = [
  { id: '24h', label: '24h', title: 'Last 24 hours' },
  { id: '7d', label: '7d', title: 'Last 7 days' },
  { id: '1mo', label: '1M', title: 'Last month' },
  { id: '3m', label: '3M', title: 'Last 3 months' },
  { id: '6m', label: '6M', title: 'Last 6 months' },
  { id: '1y', label: '1Y', title: 'Last year' },
];

/** Calendar presets under More, as a This / Previous grid. */
const CALENDAR_ROWS: Array<{ label: string; current: [DatePreset, string]; previous: [DatePreset, string] }> = [
  { label: 'Day', current: ['today', 'Today'], previous: ['yesterday', 'Yesterday'] },
  { label: 'Week', current: ['thisweek', 'This week'], previous: ['lastweek', 'Last week'] },
  { label: 'Month', current: ['mtd', 'Month to date'], previous: ['prevmonth', 'Previous month'] },
  { label: 'Year', current: ['ytd', 'Year to date'], previous: ['prevyear', 'Previous year'] },
];

/** Moves of the window already chosen - actions, so they never stay "active". */
const SHIFTS: Array<{ shift: WindowShift; label: string }> = [
  { shift: 'week', label: 'Same period last week' },
  { shift: 'month', label: 'Same period last month' },
  { shift: 'year', label: 'Same period last year' },
  { shift: 'day-last-week', label: 'Same day last week' },
];

/** Presets that live under More - so the More chip can wear the active one's name. */
const MORE_LABELS: Partial<Record<DatePreset, string>> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisweek: 'This week',
  lastweek: 'Last week',
  mtd: 'MTD',
  prevmonth: 'Prev month',
  ytd: 'YTD',
  prevyear: 'Prev year',
  max: 'All data',
  '30d': '30d',
  '15m': '15m',
  '2h': '2h',
  '3h': '3h',
  '12h': '12h',
  '48h': '48h',
};

/**
 * A rolling-window source (Live) gets the same row as EOS - six chips and More
 * - with its own numbers: windows back from now, which keep rolling while a
 * live chart ticks.
 */
const SHORT_PRESETS: Array<{ id: DatePreset; label: string; title: string }> = [
  { id: '5m', label: '5m', title: 'Last 5 minutes' },
  { id: '10m', label: '10m', title: 'Last 10 minutes' },
  { id: '30m', label: '30m', title: 'Last 30 minutes' },
  { id: '1h', label: '1h', title: 'Last hour' },
  { id: '6h', label: '6h', title: 'Last 6 hours' },
  { id: '24h', label: '24h', title: 'Last 24 hours' },
];

/** The rolling windows under a rolling-window source's More. */
const SHORT_MORE: Array<[DatePreset, string]> = [
  ['15m', 'Last 15 min'],
  ['2h', 'Last 2 hours'],
  ['3h', 'Last 3 hours'],
  ['12h', 'Last 12 hours'],
  ['48h', 'Last 48 hours'],
];

/** The prose name of a preset, for tooltips. */
function PRESET_TITLE(preset: DatePreset): string {
  return PRESET_LABELS[preset] ?? preset;
}

@customElement('date-range')
export class DateRange extends LightElement {
  private store = new StoreController(this, [
    startDate,
    endDate,
    activePreset,
    shipDateRange,
    activeDataSource,
    dataSources,
    periodComparison,
  ]);

  /**
   * Whose window this picker edits. Explore's unless a host says otherwise -
   * today that host is a detached dashboard's toolbar.
   */
  @property({ attribute: false }) scope: SelectionScope = exploreSelection;

  /** Whether the More menu is open. */
  @state() private moreOpen = false;

  /** Set while a non-atom scope is watched, so the chips follow its writes. */
  private unwatch: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.unwatch = this.scope.subscribe(() => this.requestUpdate());
  }

  /** A matching fixed range lights a chip, but stays fixed for saved links. */
  private get displayedPreset(): DatePreset {
    if (this.scope.preset() !== 'custom') return this.scope.preset();
    const presets = this.shortWindow
      ? [...SHORT_PRESETS.map((p) => p.id), ...SHORT_MORE.map(([id]) => id), 'today' as DatePreset, 'yesterday' as DatePreset]
      : [
          ...CALENDAR_ROWS.flatMap((row) => [row.current[0], row.previous[0]]),
          ...HIST_PRESETS.map((p) => p.id),
          '30d' as const,
        ];
    return matchingDatePreset({ start: this.scope.start(), end: this.scope.end() }, presets) ?? 'custom';
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.closeMore();
    this.unwatch?.();
    this.unwatch = null;
  }

  private openMore(): void {
    this.moreOpen = true;
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey);
  }

  private closeMore = (): void => {
    this.moreOpen = false;
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey);
  };

  private onOutside = (e: Event): void => {
    const wrap = this.querySelector('.preset-more-wrap');
    if (wrap && !e.composedPath().includes(wrap)) this.closeMore();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeMore();
  };

  /**
   * Picking keeps the menu open: people step through ranges here - Previous
   * month, then shift it back a year - and a menu that shuts after each click
   * makes every step two clicks. It closes on a click outside, Escape, or the
   * More chip.
   */
  private pick(preset: DatePreset): void {
    this.scope.applyPreset(preset);
  }

  private shift(shift: WindowShift): void {
    this.scope.shift(shift);
  }

  private onInputChange(which: 'start' | 'end', e: Event): void {
    const value = (e.target as HTMLInputElement).value;
    if (which === 'start') this.scope.setStart(value);
    else this.scope.setEnd(value);
  }

  /** The ship's whole range, for the All data item - or nothing before it loads. */
  private maxRange(): string {
    const range = shipDateRange.value;
    return range?.min_date && range?.max_date ? `${range.min_date} - ${range.max_date}` : '';
  }

  private presetBtn(id: DatePreset, label: string, title = ''): TemplateResult {
    const active = this.displayedPreset === id;
    return html`
      <button
        class="date-preset ${active ? 'active' : ''}"
        title=${title || nothing}
        aria-pressed=${active ? 'true' : 'false'}
        @click=${() => this.scope.applyPreset(id)}
      >
        ${label}
      </button>
    `;
  }

  /**
   * More: a chip that opens the less-used windows. When one of them is the
   * active preset the chip says which, in the active style - so the row always
   * shows what is selected, whether it came from a chip or from the menu.
   */
  private renderMore(): TemplateResult {
    const preset = this.displayedPreset;
    const activeMore = MORE_LABELS[preset];
    return html`<span class="preset-more-wrap">
      <button
        class="date-preset preset-more-btn ${activeMore ? 'active' : ''} ${this.moreOpen ? 'is-open' : ''}"
        aria-haspopup="menu"
        aria-expanded=${this.moreOpen ? 'true' : 'false'}
        title=${activeMore ? PRESET_TITLE(preset) : 'More date ranges'}
        @click=${() => (this.moreOpen ? this.closeMore() : this.openMore())}
      >
        ${activeMore ?? 'More'}<span class="preset-more-caret" aria-hidden="true"></span>
      </button>
      ${this.moreOpen ? this.renderMoreMenu() : nothing}
    </span>`;
  }

  private renderMoreMenu(): TemplateResult {
    const preset = this.displayedPreset;
    const cell = ([id, label]: [DatePreset, string]) => html`<button
      class="preset-more-item ${preset === id ? 'active' : ''}"
      role="menuitemradio"
      aria-checked=${preset === id ? 'true' : 'false'}
      @click=${() => this.pick(id)}
    >${label}</button>`;
    if (this.shortWindow) {
      // A rolling-window source: longer rolling windows, and today/yesterday.
      // No week-or-longer calendar and no shifts - live data is about now.
      const day = CALENDAR_ROWS[0]!;
      return html`<div class="preset-more-menu" role="menu">
        <span class="preset-more-head">Rolling</span>
        ${SHORT_MORE.map((item) => cell(item))}
        <div class="preset-more-sep"></div>
        <div class="preset-more-grid">
          <span class="preset-more-head">Calendar</span>
          <span class="preset-more-col">This</span>
          <span class="preset-more-col">Previous</span>
          <span class="preset-more-row">${day.label}</span>${cell(day.current)}${cell(day.previous)}
        </div>
      </div>`;
    }
    return html`<div class="preset-more-menu" role="menu">
      <div class="preset-more-grid">
        <span class="preset-more-head">Calendar</span>
        <span class="preset-more-col">This</span>
        <span class="preset-more-col">Previous</span>
        ${CALENDAR_ROWS.map(
          (row) => html`<span class="preset-more-row">${row.label}</span>${cell(row.current)}${cell(row.previous)}`,
        )}
      </div>
      <div class="preset-more-sep"></div>
      <span class="preset-more-head">Shift current window</span>
      ${SHIFTS.map(
        (s) => html`<button class="preset-more-item is-action" role="menuitem" @click=${() => this.shift(s.shift)}>
          <span aria-hidden="true">←</span> ${s.label}
        </button>`,
      )}
      ${this.scope.kind === 'explore'
        ? html`<div class="preset-more-sep"></div>
            <button
              class="preset-more-item is-action ${this.scope.preset() === 'max' ? 'active' : ''}"
              role="menuitemradio"
              aria-checked=${this.scope.preset() === 'max' ? 'true' : 'false'}
              @click=${() => this.pick('max')}
            >
              All data <span class="preset-more-hint">${this.maxRange()}</span>
            </button>`
        : nothing}
    </div>`;
  }

  /**
   * The heading row. Comparing periods is an Explore chart mode, so the SFOC
   * viewer gets the bare heading: its editor would read and write Explore's
   * comparison, not anything the SFOC window means.
   */
  private renderHeading(): TemplateResult {
    if (this.scope.kind !== 'model' && this.scope.kind !== 'live') {
      return html`<period-comparison-editor></period-comparison-editor>`;
    }
    return html`<div class="compare-entry">
      <span class="compare-entry-label"
        >${this.scope.kind === 'live' ? 'Live window' : 'Date range'}
        <span class="utc-badge" title="All times are UTC">UTC</span></span
      >
    </div>`;
  }

  /** Explore reading a source that keeps only a rolling window. */
  private get shortWindow(): boolean {
    if (this.scope.kind === 'live') return true;
    return this.scope.kind === 'explore' && !sourceHasHistory(activeDataSource.value);
  }

  override render(): TemplateResult {
    if (periodComparison.value && this.scope.kind !== 'model' && this.scope.kind !== 'live') {
      return html`<period-comparison-editor></period-comparison-editor>`;
    }
    return html`
      ${this.renderHeading()}

      ${this.shortWindow
        ? html`<div class="date-presets">
            ${SHORT_PRESETS.map((p) => this.presetBtn(p.id, p.label, p.title))} ${this.renderMore()}
          </div>`
        : html`<div class="date-presets">
            ${HIST_PRESETS.map((p) => this.presetBtn(p.id, p.label, p.title))} ${this.renderMore()}
          </div>`}

      <div class="row date-row">
        <div>
          <label for="start-date">Start</label>
          <input
            type="datetime-local"
            id="start-date"
            step="1"
            .value=${this.scope.start()}
            @change=${(e: Event) => this.onInputChange('start', e)}
          />
        </div>
        <div>
          <label for="end-date">End</label>
          <input
            type="datetime-local"
            id="end-date"
            step="1"
            .value=${this.scope.end()}
            @change=${(e: Event) => this.onInputChange('end', e)}
          />
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'date-range': DateRange;
  }
}
